/* The gallery and the upload queue: everything `services/media` fronts —
   listing, cursor pagination, presigned uploads, likes — plus the local queue
   of this browser's own in-flight rows, whose blobs live in memory and do not
   survive a reload.

   Rules this module has to keep true:
   · Every guest sees every upload (D35); you may edit and delete only your own,
     and ownership is the verified identity, not the browser (D44).
   · Video is capped at three minutes and checked BEFORE the transfer starts
     (D25) — never make a guest wait through an upload to be rejected at the end.
   · Videos never autoplay in the grid and are excluded from the slideshow (D26).
   · Organisers can feature and unfeature, reversibly, and cannot delete (D40).
   · Any guest may like any photo but their own; three likes feature it, and
     featuring latches — withdrawing a like never takes a photo off the wall
     (D52). Only an organiser can feature or unfeature by hand (D50). */

import { useEffect, useState } from 'react';
import {
  THUMB_LONG_EDGE_PX,
  type LikeResponse,
  type LikesResponse,
  type MediaItem as ServerMediaItem,
  type PhotosResponse,
} from '@mv/contracts';
import { ApiError, NotSignedIn, api } from './api';
import { needsMultipart, uploadMultipart, uploadSingle } from './transfer';
import { now } from './time';
import { getSession, type Team } from './session';

export const MAX_VIDEO_SECONDS = 180;

export type MediaKind = 'photo' | 'video';
export type UploadState = 'uploading' | 'processing' | 'done' | 'failed' | 'rejected' | 'queued';

export interface MediaItem {
  id: string;
  kind: MediaKind;
  /** The display copy — 2560px, made in the browser, never the camera original
   *  (D25 amendment). */
  src: string;
  /** Grid thumbnail — the dominant bandwidth cost on the whole system. Empty
   *  when a video's poster could not be grabbed (a codec the browser will not
   *  decode); the tile renders a blank frame with the play badge. */
  thumb: string;
  durationSec?: number;
  uploaderId: string;
  uploaderName: string;
  uploaderTeam: Team;
  caption?: string;
  eventId?: string;
  createdAt: number;
  /** On the wall and in the Featured tab (D51). Server-owned: three likes or an
   *  organiser set it, and it latches — unliking never clears it. */
  featured: boolean;
  /** Server-owned, and the one field a poll may legitimately disagree with a
   *  fresh local tap about — see `pendingLikes`. */
  likeCount: number;
  /** Absent when not featured. Never `'upload'` — nothing features on arrival,
   *  including an organiser's own uploads (D52). */
  featuredBy?: 'likes' | 'admin';
  /** An organiser took it off the wall and further likes will not put it back
   *  (D50). The UI needs it to avoid promising a promotion that cannot
   *  happen — see `PhotoDetail`. */
  featureLocked?: boolean;
  state: UploadState;
  /** 0–1 while uploading. */
  progress?: number;
  fileName?: string;
  error?: string;
}

/* Starts empty, deliberately: seeded rows would sit in the middle of a real
   presign-and-process run looking exactly like successes. The empty states
   (Gallery's EmptyFor, the kiosk hold frame) are the first thing you see. */

let items: MediaItem[] = [];
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((fn) => fn());

/**
 * Which photos this guest has liked. Declared up here rather than with
 * `toggleLike` because `absorb` needs `pendingLikes`. Replaced rather than
 * mutated, so a component holding the old Set can tell it changed.
 */
let likedIds = new Set<string>();

/**
 * Unsettled like deltas by photo id: `+1` for an unanswered tap, `-1` for an
 * untap, composing if a guest taps twice.
 *
 * `absorb`'s rule is "server data wins over the local copy", and `likeCount` is
 * the one field where that is wrong for a moment: a poll overtaking an in-flight
 * like returns the pre-write count, and the number under the heart visibly
 * counts back down. So `absorb` re-applies whatever is still pending.
 *
 * **An entry exists only while it is non-zero** — see `bumpPending`, the only
 * writer, which holds that.
 */
const pendingLikes = new Map<string, number>();

/**
 * Which account `likedIds` belongs to, and a counter invalidating replies from
 * the previous one. A like in the air when the reception laptop changes hands
 * would otherwise settle *after* the switch and put the organiser's like into
 * the next guest's `likedIds`, rendering a filled heart on a photo they have
 * never seen. Each request captures the epoch it started in and drops its reply
 * if the number moved.
 */
let likesFor: string | null = null;
let likesEpoch = 0;

let loaded = false;
let loading: Promise<void> | null = null;
let paging: Promise<void> | null = null;

/**
 * PAGING, AND WHY IT IS A CURSOR PER LIST RATHER THAN ONE.
 *
 * The gallery is several server reads (see `refreshMedia`), so it is several
 * independent positions advancing at wildly different rates: `all` runs to
 * dozens of pages, `mine` usually finishes in one, `featured` is a short shelf.
 *
 * **The cursor is a DynamoDB `LastEvaluatedKey`, not an offset** — it names the
 * row after which to continue, so photos arriving at the head, which is the
 * entire traffic pattern of a wedding, do not shift it. An offset would need
 * correcting on every poll and would still skip or repeat rows under concurrent
 * uploads.
 */
const PAGE_LIMIT = 100; // PAGE_MAX in services/media. Fewer round trips on hotel wifi.

/**
 * The FIRST read is smaller, because of the size of a row rather than the number
 * of them. D49 signs every `src` and `thumb`, and a presigned S3 URL is ~1.3 kB
 * of signature and session token, so a photo weighs ~2.9 kB of JSON of which
 * ~90% is credentials. A hundred is ~285 kB and the first load makes three such
 * reads — All, Mine and Featured — **over half a megabyte of JSON before a single
 * thumbnail starts downloading**, more than two seconds on a 260 kB/s link.
 *
 * The gallery renders 24 tiles, so 48 is two screens: enough that the sentinel
 * does not fire instantly, half the bytes in front of first paint.
 *
 * Only the first read. Polls stay at `PAGE_LIMIT` because they are background
 * work, and because the head page is the window `reconcile` speaks for —
 * shrinking every read would shrink how far back a delete or an unfeature made
 * elsewhere reaches on an already-open tab.
 *
 * **A workaround for the payload, not a fix.** Dropping D49's signing once the
 * derivatives sit behind CloudFront takes a row from ~2.9 kB to ~250 bytes,
 * which is worth more than any page size.
 */
const FIRST_PAGE = 48;

/** One key per server read. Each is a separate call with its own cursor — see
 *  `refreshMedia` for why the gallery is not one call sliced three ways. */
type ListKey = 'all' | 'mine' | 'featured';

interface ListState {
  /** Where the next page continues from. `undefined` before the first read. */
  cursor?: string;
  /** The server answered without a cursor: there is nothing further back. */
  done: boolean;
  /** `createdAt` of the oldest row in this list's most recent HEAD page — what
   *  lets a poll notice a deletion (see `reconcile`). `-Infinity` once the list
   *  fits in one page, because then the head page is the whole list. */
  headFloor: number;
}

const blank = (): ListState => ({ cursor: undefined, done: false, headFloor: Infinity });
const lists: Record<ListKey, ListState> = {
  all: blank(), mine: blank(), featured: blank(),
};

/**
 * Whose gallery is in memory. This is a module-level cache and `signOut` does
 * not empty it, which was harmless while every refresh replaced `items`
 * wholesale. With paging it is not: rows the next read is not entitled to
 * reconcile away would carry one account's rows, cursors and list position into
 * the next guest's session on the reception laptop.
 *
 * With `hidden` retired there is no row a viewer is not entitled to see (D50),
 * so this is not confidentiality — it is one account's paging state driving
 * another's gallery, and `likedIds` alongside it, which the heart would get
 * wrong. The reset below covers both.
 */
let loadedFor: string | null = null;

function resetPaging(): void {
  lists.all = blank();
  lists.mine = blank();
  lists.featured = blank();
}

/**
 * The three reads, and there is no longer an admin branch: every list here is
 * open to every guest. The hidden shelf was the only 403, and it is gone (D50).
 */
function listsFor(): ListKey[] {
  return ['all', 'mine', 'featured'];
}

function pathFor(key: ListKey, cursor?: string, limit = PAGE_LIMIT): string {
  const q = new URLSearchParams({ limit: String(limit) });
  if (key === 'mine') q.set('mine', 'true');
  if (key === 'featured') q.set('featured', 'true');
  if (cursor) q.set('cursor', cursor);
  return `/photos?${q.toString()}`;
}

/**
 * Which lists a row we hold *ought* to come back in, judged from the local copy
 * — deliberately, because the question is "the server did not return this;
 * should it have?", so the fresh answer is the thing that is missing.
 */
function claims(it: MediaItem): Record<ListKey, boolean> {
  return {
    all: true,
    mine: false, // never reconciled — see RECONCILABLE
    featured: it.featured, // also never reconciled — see RECONCILABLE
  };
}

/**
 * The only lists allowed to delete a row — a restriction that fixes a bug which
 * deleted eight live photographs in testing.
 *
 * `all` is the whole gallery now — GSI2 is partitioned on a constant, so the
 * read returns every photograph and "would this row have been in it" is simply
 * yes. That makes it the strongest reconciler this store has ever had, and the
 * only one it needs.
 *
 * **`mine` must not reconcile.** It is a different index (GSI1, `uploaderId`)
 * answering a different question, so the client predicate is a *guess* at what
 * that query returns. Once `mine` fits in one
 * page its floor drops to `-Infinity` and that guess gets authority over every
 * row in the store: measured against 250 photographs, one poll removed eight of
 * them permanently, with no error anywhere. It still runs, still merges, and
 * still protects its own rows from the other two — it just gets no vote.
 *
 * **`featured` is left out too, and not because it is unsafe.** `it.featured`
 * matches the server's `feat` key exactly. It is excluded because it adds only
 * a second opinion on whether a row still exists, which is the shape of the bug
 * above. Unfeaturing reaches other phones through the gallery read refreshing
 * `featured` on the row; the row *should* stay, because it is still in the
 * gallery, and only the Featured tab's predicate changes.
 *
 * NOTE FOR ANYONE TEMPTED: no test fails if you add it, because `absorb` runs
 * before `reconcile`, so a row that dropped out of the featured list has already
 * had `featured` refreshed and never claims it. That is a property of today's
 * read shapes, not a licence — it becomes reachable the moment the two reads can
 * disagree, which is what the retirement merge changes.
 */
const RECONCILABLE: ListKey[] = ['all'];

/**
 * A server row with any unconfirmed local like put back on top. Clamped at zero:
 * an untap racing the poll can otherwise read `-1` for an instant, and "−1
 * likes" under a photograph is the kind of detail a guest screenshots.
 */
function withPendingLike(row: MediaItem): MediaItem {
  const delta = pendingLikes.get(row.id);
  if (!delta) return row;
  return { ...row, likeCount: Math.max(0, row.likeCount + delta) };
}

/** Rows in, `items` out, merged by id. Server data wins over the local copy of
 *  the same row — except for an unsettled like, which is newer than the read
 *  that overtook it; anything still uploading from this browser is untouched. */
function absorb(rows: ServerMediaItem[]): void {
  const fresh = new Map(rows.map((r) => [r.id, withPendingLike({ ...r, state: 'done' as const })]));
  const merged = items.map((it) => {
    if (it.state !== 'done') return it; // in flight — not on the server yet
    const row = fresh.get(it.id);
    if (!row) return it;
    fresh.delete(it.id);
    return row;
  });
  items = [...merged, ...fresh.values()];
}

/**
 * Drop rows the server has stopped returning — the only way a delete made on
 * somebody else's phone reaches this one. A merge alone only ever adds, so a
 * photo its owner deleted would stay on every other guest's gallery, and an
 * unfeatured one on the projector, until they reloaded.
 *
 * The rule: a row is dropped when some list just re-read *should* have contained
 * it and did not. `headFloor` keeps that safe — the head page only speaks for
 * rows as old as its oldest, so anything older is beyond what this read saw.
 *
 * KNOWN LIMIT, deliberate: only the head window is reconciled, so a photo
 * deleted after it scrolled out of the newest hundred stays visible on an
 * already-open tab until reload. The alternative refetches every page loaded so far on every
 * poll, unbounded over an evening and multiplied by every guest. Moderation
 * lands on photos somebody just uploaded, which is the window this covers.
 */
function reconcile(fetched: ListKey[], returned: Set<string>): void {
  const judging = fetched.filter((k) => RECONCILABLE.includes(k));
  items = items.filter((it) => {
    if (it.state !== 'done') return true;
    if (returned.has(it.id)) return true;
    const claim = claims(it);
    return !judging.some((k) => claim[k] && it.createdAt >= lists[k].headFloor);
  });
}

/**
 * Pull the head of the gallery from the API.
 *
 * **A read per tab, not one read sliced up.** Each answers from its own index
 * and its own cursor, so a tab is never a filtered slice of a page that stopped
 * short of the rows it wanted:
 *
 *   `/photos`               every photo, newest first (GSI2 on the constant `all`)
 *   `/photos?mine=true`     the caller's own uploads (GSI1)
 *   `/photos?featured=true` the Featured tab (sparse GSI3 — D51)
 *
 * All three are open to every guest. Liked is the exception and is not a read:
 * it is the ids from `/likes` intersected with rows already in hand.
 *
 * **Head only, never moving a cursor.** New photos arrive at the front, and a
 * poll that also walked the back pages would redo the evening's scrolling every
 * twenty seconds. `loadMoreMedia` owns the cursors, and the two do not interfere
 * because a `LastEvaluatedKey` is undisturbed by inserts ahead of it.
 *
 * Server rows replace server rows; anything in flight from this browser is kept,
 * because dropping it would make a guest's own upload vanish mid-transfer.
 */
export async function refreshMedia(): Promise<void> {
  // Read straight from the session rather than threading a viewer through seven
  // call sites. `session.ts` does not import this module, so no cycle.
  const viewer = getSession();
  const keys = listsFor();

  // A different account than the rows in hand belong to: start clean rather
  // than merge two people's galleries.
  const who = viewer?.userId ?? null;
  if (who !== loadedFor) {
    items = items.filter((it) => it.state !== 'done'); // keep this browser's own uploads
    resetPaging();
    loadedFor = who;
    // Likes belong to the account, not the browser — the same guarantee
    // `resetPaging` gives the rows. The epoch bump stops a reply already in the
    // air from putting them back.
    likedIds = new Set();
    pendingLikes.clear();
    likesFor = null;
    likesEpoch += 1;
  }

  const limit = loaded ? PAGE_LIMIT : FIRST_PAGE;

  /**
   * Likes are read once per account, not every poll. `GET /likes/mine` is
   * complete rather than paginated, so polling it re-sends the whole set every
   * twenty seconds to learn nothing — only a tap on this device changes it. The
   * cost is a like made on a second phone not showing until a reload.
   *
   * A failure leaves `likesFor` unset so the next poll retries, and does not
   * take the gallery with it: empty hearts beat no photographs.
   */
  const wantLikes = who !== null && likesFor !== who;
  // Captured before the reads and checked after: two polls can overlap, and the
  // account can change in between — the window `toggleLike` guards, from the
  // other direction.
  const epoch = likesEpoch;
  const [pages, likes] = await Promise.all([
    Promise.all(keys.map((k) => api<PhotosResponse>(pathFor(k, undefined, limit)))),
    wantLikes ? api<LikesResponse>('/likes/mine').catch(() => null) : Promise.resolve(null),
  ]);

  if (likes && epoch === likesEpoch && likesFor !== who) {
    const settled = new Set(likes.photoIds);
    // A tap made while this read was in the air is newer than the read.
    for (const [id, delta] of pendingLikes) {
      if (delta > 0) settled.add(id);
      else if (delta < 0) settled.delete(id);
    }
    likedIds = settled;
    likesFor = who;
  }

  const returned = new Set<string>();
  keys.forEach((key, i) => {
    const page = pages[i];
    const state = lists[key];
    for (const row of page.items) returned.add(row.id);

    // A page with no cursor IS the whole list, so the floor drops away and the
    // reconcile below speaks for every row. With a cursor, this read only saw
    // as far back as its oldest row.
    state.headFloor = page.cursor && page.items.length > 0
      ? Math.min(...page.items.map((r) => r.createdAt))
      : -Infinity;

    // Only the first read sets where paging starts; after that the cursor
    // belongs to `loadMoreMedia`, which may be many pages deeper.
    if (state.cursor === undefined && !state.done) {
      state.cursor = page.cursor;
      state.done = !page.cursor;
    }
  });

  absorb(pages.flatMap((p) => p.items));
  reconcile(keys, returned);

  loaded = true;
  emit();
}

/** Whether any list still has pages the server has not been asked for. */
export function hasMoreMedia(): boolean {
  return listsFor().some((k) => !lists[k].done);
}

/**
 * Walk one page further back in every list that has one — all together rather
 * than only the visible tab. `mine` and `featured` exhaust after a page or two
 * and are never requested again, so the cost is one extra small read; paging per tab
 * instead means switching from All to Mine lands on a half-loaded list with its
 * own scroll position and end-of-list state to get right.
 *
 * Concurrent calls collapse onto one promise: the sentinel driving this can fire
 * several times while a page is in the air, and two reads from the same cursor
 * would fetch the same rows twice.
 */
export function loadMoreMedia(): Promise<void> {
  if (paging) return paging;
  const keys = listsFor().filter((k) => !lists[k].done && lists[k].cursor);
  if (keys.length === 0) return Promise.resolve();

  paging = (async () => {
    const pages = await Promise.all(keys.map((k) => api<PhotosResponse>(pathFor(k, lists[k].cursor))));
    keys.forEach((key, i) => {
      const page = pages[i];
      lists[key].cursor = page.cursor;
      lists[key].done = !page.cursor;
    });
    // Appended, never reconciled: these rows are older than every head floor,
    // so there is nothing here this read may judge.
    absorb(pages.flatMap((p) => p.items));
    emit();
  })().finally(() => { paging = null; });

  return paging;
}

/**
 * Load the entire gallery, page after page. For the projector only: `Display`
 * shuffles the whole pool into a deck, so a partial gallery is photographs that
 * never once go on the screen, indistinguishable from the deck not having come
 * round to theirs. Affordable because it is one laptop on mains power and rows
 * are metadata — image bytes are fetched by the `<img>` for the slide shown.
 */
export async function loadAllMedia(): Promise<void> {
  // The head first, or there are no cursors to walk. `useMedia` usually has it
  // in flight, so join that read rather than starting a second.
  if (loading) await loading.catch(() => {});
  if (!loaded) await refreshMedia();

  // Bounded so a paging bug cannot become an infinite request loop on a machine
  // nobody is watching. 200 pages is 20,000 photos.
  for (let i = 0; i < 200 && hasMoreMedia(); i++) {
    const before = items.length;
    await loadMoreMedia();
    if (items.length === before) break; // no progress — stop rather than spin
  }
}

/**
 * Walk the featured list to its end, and nothing else — what `loadAllMedia` is
 * for the gallery. D51 puts only featured photos on the screen, so pulling every
 * page of the week was six thousand ~2.9 kB signed rows for a list the kiosk
 * threw most of away. This reads the sparse index instead, leaving the gallery's
 * own cursors where the scroll left them.
 */
export async function loadAllFeatured(): Promise<void> {
  if (loading) await loading.catch(() => {});
  if (!loaded) await refreshMedia();

  // Same bound and reason as `loadAllMedia`.
  for (let i = 0; i < 200; i++) {
    // `loadMoreMedia` advances this cursor too; waiting is cheaper than both
    // reading the same page.
    if (paging) await paging.catch(() => {});

    // Read fresh every lap, never captured: `resetPaging` REPLACES these
    // objects, so a walk holding a reference across a change of account would
    // keep advancing an orphan, pouring the previous viewer's pages into the new
    // one's gallery from a cursor nothing else can see.
    const state = lists.featured;
    if (state.done || !state.cursor) break;

    const from = state.cursor;
    const page = await api<PhotosResponse>(pathFor('featured', from));

    // Whoever we were reading for is gone; these rows are theirs, so drop
    // rather than merge them.
    if (lists.featured !== state) break;

    absorb(page.items);
    emit();

    // Somebody moved the cursor while that page was in the air. Overwriting
    // theirs would re-walk a page (harmless) or declare the list finished early
    // (not), so hand over instead.
    if (state.cursor !== from) break;
    state.cursor = page.cursor;
    state.done = !page.cursor;
  }
}

/** The projector's data hook: everything, once, then the ordinary poll keeps
 *  the head fresh. */
export function useAllMedia(): MediaItem[] {
  const media = useMedia();
  useEffect(() => {
    void loadAllMedia().catch(() => { /* Offline. The deck plays what arrived. */ });
  }, []);
  return media;
}

/**
 * The projector's data hook under D51: the featured pool loaded to its end, then
 * the ordinary poll keeps the head fresh. Returns the whole store rather than
 * only featured rows — `Display` filters, and the gallery reads that came with
 * the walk are already paid for. What changes against `useAllMedia` is what gets
 * *fetched*: one sparse cursor, not every page of the week.
 */
export function useAllFeatured(): MediaItem[] {
  const media = useMedia();
  useEffect(() => {
    void loadAllFeatured().catch(() => { /* Offline. The deck plays what arrived. */ });
  }, []);
  return media;
}

export function useMedia(): MediaItem[] {
  const [, bump] = useState(0);
  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    listeners.add(fn);
    if (!loaded && !loading) {
      loading = refreshMedia()
        .catch(() => { /* Offline or signed out. The empty state says so. */ })
        .finally(() => { loading = null; });
    }
    return () => { listeners.delete(fn); };
  }, []);
  return items;
}

/** True once the first load has come back, so the gallery can tell "empty"
 *  apart from "not asked yet" and show a skeleton rather than "no photos". */
export const mediaLoaded = () => loaded;

/**
 * Poll for what other people have uploaded. Nothing pushes them here — there is
 * no socket, by design (D12/D17: static files and stateless reads).
 *
 * Skipped while the tab is hidden, so a phone in a pocket is not doing this all
 * night, and fired once immediately on becoming visible again.
 */
export function useMediaPolling(everyMs: number): void {
  useEffect(() => {
    let cancelled = false;

    const pull = () => {
      if (cancelled || document.hidden) return;
      void refreshMedia().catch(() => { /* Offline. The last copy stays up. */ });
    };

    const id = setInterval(pull, everyMs);
    const onVisible = () => { if (!document.hidden) pull(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [everyMs]);
}

export function getItem(id: string): MediaItem | undefined {
  return items.find((it) => it.id === id);
}

/**
 * Newest first, minus this browser's own rejected files. Nothing else is
 * filtered: every guest sees every upload (D35), and with `hidden` retired
 * there is no row a viewer is not entitled to (D50).
 */
export function visibleTo(all: MediaItem[]) {
  return all
    .filter((it) => it.state !== 'rejected')
    .sort((a, b) => b.createdAt - a.createdAt);
}

function patch(id: string, changes: Partial<MediaItem>) {
  items = items.map((it) => (it.id === id ? { ...it, ...changes } : it));
  emit();
}

/* Mutations are optimistic: the local row changes at once and the request
   follows. A failure is recoverable by trying again, whereas a spinner on every
   caption keystroke is not. */

export function setCaption(id: string, caption: string) {
  const next = caption.trim() || undefined;
  patch(id, { caption: next });
  void api(`/photos/${id}`, { method: 'PATCH', json: { caption: next ?? '' } })
    .catch(() => { void refreshMedia().catch(() => {}); });
}


/**
 * Put a photo on the wall or take it off. Organisers only, server-enforced
 * (D50).
 *
 * The route answers `{ ok, changed }`, and `changed: false` is deliberately not
 * a failure here: it means the photo was already in the state asked for, so the
 * optimistic patch is already right. A caller wanting to say "already featured"
 * belongs with the button, not the store.
 */
export function setFeatured(id: string, featured: boolean) {
  patch(id, { featured, featuredBy: featured ? 'admin' : undefined });
  void api(`/photos/${id}/feature`, { method: 'POST', json: { featured } })
    .catch(() => { void refreshMedia().catch(() => {}); });
}

/**
 * Move a photo's pending delta and return where it ended up.
 *
 * The one invariant: **an entry exists only while it is non-zero.** Storing a
 * settled pair as a literal `0` would work only because `withPendingLike` tests
 * `!delta` — swap that for an `undefined` check and a dormant zero starts
 * subtracting. Cheaper not to have the state than to remember why it is
 * harmless.
 */
function bumpPending(id: string, delta: number): number {
  const next = (pendingLikes.get(id) ?? 0) + delta;
  if (next === 0) pendingLikes.delete(id);
  else pendingLikes.set(id, next);
  return next;
}

/** Retire this request's share of the pending delta and return what is LEFT,
 *  not zero: a guest can tap twice before the first reply lands, and the second
 *  tap is still unsettled on top of whatever count the first carried. */
const dropPending = (id: string, delta: number): number => bumpPending(id, -delta);

/** Every photo this guest has liked. Every heart in the app renders from this
 *  one Set rather than a field on each row — see `LikesResponse` on why a
 *  `likedByMe` boolean would have been expensive. */
export function useLiked(): Set<string> {
  const [, bump] = useState(0);
  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
  return likedIds;
}

/**
 * Like a photo, or take a like back (D52).
 *
 * Optimistic like every mutation here, except that `likeCount` is server-owned,
 * so the optimism has to survive a poll landing mid-flight — that is
 * `pendingLikes`, re-applied in `absorb`.
 *
 * Refused before sending in the two cases the server would refuse: a row still
 * uploading has no server id, and a *guest's* self-like is a 400 because at a
 * threshold of three it would make the real bar two (D52).
 *
 * **A failure is put back by arithmetic, not by refetching.** The photo may be a
 * thousand rows deep, past where a head-page poll reaches, so a refresh would
 * leave the count wrong exactly where it is hardest to notice.
 */
export function toggleLike(id: string): void {
  const viewer = getSession();
  if (!viewer) return;
  const item = items.find((it) => it.id === id);
  if (!item || item.state !== 'done') return;
  // A guest may not like their own photo. An organiser may: their like is the
  // promotion rather than a vote, and it is how they curate their own uploads
  // (D52 as amended). **This guard must agree with `LikeButton`'s `canAct` and
  // with the route** — three copies of one rule, and when they disagree the UI
  // offers a live control that silently does nothing.
  if (!viewer.isAdmin && item.uploaderId === viewer.userId) return;

  const liked = !likedIds.has(id);
  const delta = liked ? 1 : -1;
  const epoch = likesEpoch;

  const next = new Set(likedIds);
  if (liked) next.add(id);
  else next.delete(id);
  likedIds = next;
  bumpPending(id, delta);
  patch(id, { likeCount: Math.max(0, item.likeCount + delta) });

  void api<LikeResponse>(`/photos/${id}/like`, { method: 'POST', json: { liked } })
    .then((res) => {
      if (epoch !== likesEpoch) return; // a different account is signed in now
      const rest = dropPending(id, delta);
      /* `featured` especially cannot be guessed: unfeaturing leaves a lock
         three further likes deliberately do not undo (D50), so a client that
         promoted the tile by arithmetic would be wrong until the next poll. */
      patch(id, { likeCount: Math.max(0, res.likeCount + rest), featured: res.featured });
    })
    .catch(() => {
      if (epoch !== likesEpoch) return;
      const rest = dropPending(id, delta);
      // Only when nothing else is pending: a later tap is a newer statement of
      // intent that this failure does not overrule.
      if (rest === 0) {
        const back = new Set(likedIds);
        if (liked) back.delete(id);
        else back.add(id);
        likedIds = back;
      }
      const current = items.find((it) => it.id === id);
      if (current) patch(id, { likeCount: Math.max(0, current.likeCount - delta) });
      else emit();
    });
}

/** Local only. Used by `cancel` and by the server-delete path below. */
function forget(id: string) {
  const item = items.find((it) => it.id === id);
  if (item?.src.startsWith('blob:')) URL.revokeObjectURL(item.src);
  if (item?.thumb.startsWith('blob:')) URL.revokeObjectURL(item.thumb);
  items = items.filter((it) => it.id !== id);
  emit();
}

export function remove(id: string) {
  const item = items.find((it) => it.id === id);
  forget(id);
  // A row that never reached the server has nothing to delete there.
  if (item?.state === 'done') {
    void api(`/photos/${id}`, { method: 'DELETE' })
      .catch(() => { void refreshMedia().catch(() => {}); });
  }
}

/** Cancel is not delete: it stops an upload still in flight and leaves the
 *  server alone, because nothing was written there. */
export function cancel(id: string) {
  aborters.get(id)?.abort();
  aborters.delete(id);
  forget(id);
}

/* ── uploading ─────────────────────────────────────────────────────────────*/

export class RejectedFile extends Error {
  constructor(public fileName: string, public reason: string) { super(reason); }
}

/** Reads duration from the file itself, before a byte is uploaded (D25). */
export function videoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement('video');
    el.preload = 'metadata';
    el.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(el.duration); };
    el.onerror = () => { URL.revokeObjectURL(url); reject(new Error('unreadable')); };
    el.src = url;
  });
}

/** Long edge of the copy guests are served. The projector is 1080p, so 2560 is
 *  a downscale with room to spare — everything above it is pixels nobody can
 *  see (D25 amendment). */
const DISPLAY_LONG_EDGE = 2560;
const THUMB_LONG_EDGE = 480;

/**
 * Encode an already-decoded bitmap down to `longEdge`. Split from the decode
 * deliberately: decoding a 24-megapixel phone photo is the expensive half and it
 * used to run once per output size. Decode once, encode twice.
 *
 * `OffscreenCanvas.convertToBlob` keeps the encode off the main thread where the
 * browser has it (Chrome, Safari 16.4+); `canvas.toBlob` is the fallback and
 * janks, which is the price of an old browser rather than of every browser.
 */
async function encodeFrom(bitmap: ImageBitmap, longEdge: number, quality: number): Promise<Blob | null> {
  // Never upscale: inflating a source already inside the bound adds bytes and
  // no detail.
  const scale = Math.min(1, longEdge / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));

  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(w, h);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, w, h);
    try {
      return await canvas.convertToBlob({ type: 'image/webp', quality });
    } catch {
      return null;
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(bitmap, 0, 0, w, h);
  return await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((b) => resolve(b), 'image/webp', quality);
  });
}

/**
 * Decode to a bitmap, or null if the browser cannot read it — desktop Chrome
 * cannot decode HEIC, and an iPhone photo AirDropped to a laptop is one.
 *
 * Null means "send the original untouched": slower, never worse. It must never
 * mean "fail the upload".
 */
async function decode(file: Blob): Promise<ImageBitmap | null> {
  try {
    return await createImageBitmap(file);
  } catch {
    return null;
  }
}

/**
 * The two artefacts an upload produces, both made here rather than server-side
 * (D25 amendment): `display` for the gallery, detail view and projector, `thumb`
 * for the grid tile. The full-resolution original is not uploaded — at ~4 MB it
 * is sixteen seconds of hotel wifi per photo against four, for detail below what
 * a 1080p screen resolves.
 */
export async function derivativesFor(file: File): Promise<{ display: Blob; thumb: Blob | null }> {
  if (!file.type.startsWith('image/')) return { display: file, thumb: null };
  const bitmap = await decode(file);
  if (!bitmap) return { display: file, thumb: null };
  try {
    const alreadySmall = Math.max(bitmap.width, bitmap.height) <= DISPLAY_LONG_EDGE;
    const display = alreadySmall ? null : await encodeFrom(bitmap, DISPLAY_LONG_EDGE, 0.82);
    const thumb = await encodeFrom(bitmap, THUMB_LONG_EDGE, 0.7);
    return { display: display ?? file, thumb };
  } finally {
    bitmap.close();
  }
}

/**
 * A video's poster frame, as an object URL for the tile and a blob to upload:
 * a video has no display copy until MediaConvert has run, so the poster is all
 * the gallery can show. Photos get theirs from `derivativesFor` instead.
 */
interface Poster {
  url: string;
  blob: Blob | null;
  /** Source pixel dimensions, signed into the upload as S3 metadata so the
   *  transcoder can size its output without upscaling (`media-processor`'s
   *  `outputSize`). The only place in the browser that learns them. */
  width?: number;
  height?: number;
}

async function posterFrame(url: string): Promise<Poster> {
  return new Promise((resolve) => {
    const el = document.createElement('video');
    el.preload = 'metadata';
    el.muted = true;
    el.src = url;
    const nothing: Poster = { url: '', blob: null };

    el.onloadeddata = () => {
      try {
        el.currentTime = Math.min(0.5, el.duration / 2);
      } catch { resolve(nothing); }
    };
    el.onseeked = () => {
      const vw = el.videoWidth;
      const vh = el.videoHeight;
      if (!vw || !vh) return resolve(nothing);

      // Scale the LONG edge, not the width. Pinning width made a portrait clip
      // — most of them — a 480x853 poster: nearly twice the pixels asked for,
      // on the largest bandwidth cost in the system.
      const scale = Math.min(1, THUMB_LONG_EDGE_PX / Math.max(vw, vh));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(vw * scale);
      canvas.height = Math.round(vh * scale);

      const ctx = canvas.getContext('2d');
      if (!ctx) return resolve(nothing);
      ctx.drawImage(el, 0, 0, canvas.width, canvas.height);
      // WebP to match what the thumb target's presign policy expects.
      canvas.toBlob(
        (blob) => resolve(blob
          ? { url: URL.createObjectURL(blob), blob, width: vw, height: vh }
          : nothing),
        'image/webp',
        0.7,
      );
    };
    el.onerror = () => resolve(nothing);
  });
}

export interface Uploader { userId: string; name: string; team: Team }

/** Adds files to the queue. Anything unacceptable is turned away here, with a
 *  sentence, rather than after a three-minute wait. */
export async function enqueue(files: File[], uploader: Uploader, caption?: string, eventId?: string) {
  const posterWork: (() => Promise<void>)[] = [];

  for (const file of files) {
    const id = `up-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');

    if (!isVideo && !isImage) {
      items = [{
        id, kind: 'photo', src: '', thumb: '', uploaderId: uploader.userId,
        uploaderName: uploader.name, uploaderTeam: uploader.team, createdAt: now(),
        featured: false, likeCount: 0,
        state: 'rejected', fileName: file.name,
        error: 'That is not a photo or a video, so there is nothing to add.',
      }, ...items];
      emit();
      continue;
    }

    const url = URL.createObjectURL(file);
    let durationSec: number | undefined;

    if (isVideo) {
      try {
        durationSec = await videoDuration(file);
      } catch {
        durationSec = undefined;
      }
      if (durationSec && durationSec > MAX_VIDEO_SECONDS) {
        URL.revokeObjectURL(url);
        items = [{
          id, kind: 'video', src: '', thumb: '', durationSec,
          uploaderId: uploader.userId, uploaderName: uploader.name, uploaderTeam: uploader.team,
          createdAt: now(), featured: false, likeCount: 0,
          state: 'rejected', fileName: file.name,
          error: `That one is ${formatDuration(durationSec)} long. Videos have to be under three minutes — nothing was uploaded.`,
        }, ...items];
        emit();
        continue;
      }
    }

    // The row appears NOW, with no poster. Generating it first meant every
    // earlier file's decode had to finish before the tenth appeared — pick
    // twenty and the last was invisible for most of a minute, which reads
    // exactly like a dropped upload.
    items = [{
      id, kind: isVideo ? 'video' : 'photo', src: url, thumb: '', durationSec,
      uploaderId: uploader.userId, uploaderName: uploader.name, uploaderTeam: uploader.team,
      caption, eventId, createdAt: now(),
      featured: false, likeCount: 0,
      state: 'queued', progress: 0, fileName: file.name,
    }, ...items];
    emit();

    posterWork.push(async () => {
      if (isVideo) {
        const poster = await posterFrame(url);
        if (!items.some((it) => it.id === id)) return;
        if (poster.url) patch(id, { thumb: poster.url });
        // The clip itself goes up; there is no browser-side transcode. Above
        // the multipart threshold `run` takes the resumable path.
        pending.set(id, {
          kind: 'video', contentType: file.type, primary: file,
          thumb: poster.blob, durationSec, caption, eventId,
          width: poster.width, height: poster.height,
        });
        void run(id);
        return;
      }

      // Derivatives before anything is sent; the camera original never leaves
      // the phone (D25). Measured, a 21 MB file becomes a 0.8 MB upload.
      const { display, thumb } = await derivativesFor(file);
      if (!items.some((it) => it.id === id)) return;
      patch(id, { thumb: thumb ? URL.createObjectURL(thumb) : url });
      pending.set(id, {
        kind: 'photo',
        // What is actually being sent: WebP normally, the untouched file when
        // the browser could not decode it (HEIC on desktop Chrome). **It must
        // match the blob exactly** — the wrong type fails the POST policy's
        // Content-Type condition rather than the transfer.
        contentType: display === file ? file.type : 'image/webp',
        primary: display,
        thumb,
        caption,
        eventId,
      });
      void run(id);
    });
  }

  await pooled(posterWork, MAX_CONCURRENT_DECODES);
}

/** Run tasks `limit` at a time. Decoding is CPU-bound, so more workers only
 *  compete with the render thread and make the queue stutter as it scrolls. */
async function pooled(tasks: (() => Promise<void>)[], limit: number): Promise<void> {
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, tasks.length) }, async () => {
    for (let i = next++; i < tasks.length; i = next++) await tasks[i]!();
  });
  await Promise.all(workers);
}

const running = new Set<string>();

/** Live uploads, so cancel stops one mid-flight rather than hiding the row
 *  while the bytes keep going. */
const aborters = new Map<string, AbortController>();

/**
 * How many uploads are in flight at once. On a bandwidth-bound link this does
 * not make the total faster — measured, 20 files take the same wall-clock at 1,
 * 3 or 6. What it buys is the per-file overhead: a presign round-trip and a TLS
 * handshake each, most of a second on a resort connection, previously in series.
 * And one stalled transfer no longer holds up the nineteen behind it.
 */
const MAX_CONCURRENT_TRANSFERS = 3;

/** Decodes are CPU-bound; a separate budget from the network one. */
const MAX_CONCURRENT_DECODES = 2;

let activeTransfers = 0;
const waitingForSlot: (() => void)[] = [];

function acquireSlot(): Promise<void> {
  if (activeTransfers < MAX_CONCURRENT_TRANSFERS) {
    activeTransfers += 1;
    return Promise.resolve();
  }
  // Queued. The releasing transfer hands its slot straight over rather than
  // decrementing, so the count cannot drift (`releaseSlot`).
  return new Promise<void>((resolve) => waitingForSlot.push(resolve));
}

function releaseSlot(): void {
  const next = waitingForSlot.shift();
  if (next) next();
  else activeTransfers -= 1;
}

interface Pending {
  kind: MediaKind;
  contentType: string;
  primary: Blob;
  thumb: Blob | null;
  durationSec?: number;
  /** Video only. Source dimensions, on their way to becoming S3 metadata. */
  width?: number;
  height?: number;
  caption?: string;
  eventId?: string;
}

/** What each queued row still needs to be sent, kept off the item so blobs are
 *  not handed to every component that renders a tile. */
const pending = new Map<string, Pending>();

async function run(localId: string) {
  if (running.has(localId)) return;
  running.add(localId);

  await acquireSlot();

  const work = pending.get(localId);
  if (!work || !items.some((it) => it.id === localId)) {
    running.delete(localId);
    releaseSlot();
    return;
  }

  const controller = new AbortController();
  aborters.set(localId, controller);
  patch(localId, { state: 'uploading', progress: 0, error: undefined });

  try {
    const onProgress = (f: number) => patch(localId, { progress: f });

    const photoId = needsMultipart(work.primary.size)
      ? await uploadMultipart({
        file: work.primary,
        contentType: work.contentType,
        durationSec: work.durationSec,
        width: work.width,
        height: work.height,
        thumb: work.thumb,
        onProgress,
        signal: controller.signal,
      })
      : await uploadSingle({
        kind: work.kind,
        contentType: work.contentType,
        primary: work.primary,
        thumb: work.thumb,
        durationSec: work.durationSec,
        width: work.width,
        height: work.height,
        onProgress,
        signal: controller.signal,
      });

    // The bytes are in S3 but the row does not exist until this returns, so an
    // upload that dies halfway leaves no half-photo to explain.
    patch(localId, { state: 'processing', progress: 1 });
    await api('/photos/complete', {
      method: 'POST',
      json: {
        photoId,
        kind: work.kind,
        durationSec: work.durationSec,
        caption: work.caption,
        eventId: work.eventId,
      },
    });

    patch(localId, { state: 'done', progress: 1 });
    pending.delete(localId);

    // Pick up the server's copy: its id, its attribution, and the URL the
    // gallery serves rather than this browser's blob.
    void refreshMedia().catch(() => {});
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      // Cancelled. `cancel` has already removed the row.
    } else if (err instanceof NotSignedIn) {
      patch(localId, { state: 'failed', error: 'Your sign-in ran out. Sign in again and this will go up.' });
    } else if (!navigator.onLine) {
      patch(localId, { state: 'failed', error: "The wifi dropped out. Try again when it's back." });
    } else {
      patch(localId, {
        state: 'failed',
        error: err instanceof ApiError ? err.message : 'That did not go up. Try again.',
      });
    }
  } finally {
    aborters.delete(localId);
    releaseSlot();
    running.delete(localId);
  }
}

export function retry(id: string) {
  if (!pending.has(id)) return;
  patch(id, { state: 'queued', progress: 0, error: undefined });
  void run(id);
}


/** Anything the wifi dropped picks itself back up (principle 5: be trustworthy). */
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    items.filter((it) => it.state === 'failed').forEach((it) => retry(it.id));
  });
}

export function formatDuration(seconds: number): string {
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function estimateLabel(item: MediaItem, bytesPerSecond = 260_000): string {
  if (item.state !== 'uploading' || item.progress === undefined) return '';
  const remaining = (1 - item.progress) * (1_600_000 / bytesPerSecond);
  if (remaining < 5) return 'a few seconds left';
  if (remaining < 90) return `${Math.round(remaining)} seconds left`;
  return `about ${Math.round(remaining / 60)} minutes left`;
}
